// frontend/src/components/organization/OrganizationSwitcher.js
import React, { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Business as BusinessIcon,
  ExpandMore as ExpandMoreIcon,
  Check as CheckIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '../../contexts/OrganizationContext';

const tierColors = {
  free: 'default',
  starter: 'info',
  plus: 'primary',
  pro: 'secondary',
  business: 'warning',
  enterprise: 'success'
};

const tierLabels = {
  free: 'Free',
  starter: 'Starter',
  plus: 'Plus',
  pro: 'Pro',
  business: 'Business',
  enterprise: 'Enterprise'
};

const OrganizationSwitcher = ({ variant = 'button', showTier = true }) => {
  const navigate = useNavigate();
  const {
    currentOrganization,
    organizations,
    switchOrganization,
    setPrimaryOrganization,
    loading
  } = useOrganization();

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSwitchOrg = async (orgId) => {
    await switchOrganization(orgId);
    handleClose();
  };

  const handleSetPrimary = async (event, orgId) => {
    event.stopPropagation();
    await setPrimaryOrganization(orgId);
  };

  // Defer navigation to the next tick so MUI's Menu unmounts before the
  // destination page renders. Otherwise the closing Modal's Backdrop is
  // still in the DOM when the new route mounts and swallows pointer +
  // keyboard events on the first render — users see the form but can't
  // type or click into fields until they F5 or the Backdrop cleans up.
  const handleCreateOrg = () => {
    handleClose();
    setTimeout(() => navigate('/organizations/new'), 0);
  };

  const handleManageOrg = () => {
    handleClose();
    setTimeout(() => navigate('/organizations/settings'), 0);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <BusinessIcon color="action" />
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  if (!currentOrganization) {
    return (
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleCreateOrg}
        size="small"
      >
        Create Organization
      </Button>
    );
  }

  const getOrgAvatar = (org) => {
    if (org.logoUrl) {
      return <Avatar src={org.logoUrl} sx={{ width: 24, height: 24 }} />;
    }
    return (
      <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main', fontSize: '0.75rem' }}>
        {org.name.charAt(0).toUpperCase()}
      </Avatar>
    );
  };

  return (
    <>
      <Button
        onClick={handleClick}
        endIcon={<ExpandMoreIcon />}
        sx={{
          textTransform: 'none',
          color: 'text.primary',
          '&:hover': {
            backgroundColor: 'action.hover'
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getOrgAvatar(currentOrganization)}
          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
              {currentOrganization.name}
            </Typography>
            {showTier && (
              <Chip
                label={tierLabels[currentOrganization.tier] || 'Free'}
                size="small"
                color={tierColors[currentOrganization.tier] || 'default'}
                sx={{ height: 16, fontSize: '0.65rem', mt: 0.25 }}
              />
            )}
          </Box>
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            minWidth: 280,
            maxHeight: 400
          }
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Your Organizations
          </Typography>
        </Box>

        {organizations.map((org) => (
          <MenuItem
            key={org.id}
            onClick={() => handleSwitchOrg(org.id)}
            selected={org.id === currentOrganization.id}
            sx={{ py: 1 }}
          >
            <ListItemIcon>
              {getOrgAvatar(org)}
            </ListItemIcon>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {org.name}
                  {org.membership?.isPrimary && (
                    <Chip
                      label="Primary"
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ height: 18, fontSize: '0.6rem' }}
                    />
                  )}
                </Box>
              }
              secondary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Chip
                    label={tierLabels[org.tier] || 'Free'}
                    size="small"
                    color={tierColors[org.tier] || 'default'}
                    sx={{ height: 16, fontSize: '0.6rem' }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {org.membership?.role}
                  </Typography>
                </Box>
              }
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {org.id === currentOrganization.id && (
                <CheckIcon color="primary" fontSize="small" />
              )}
              <Tooltip title={org.membership?.isPrimary ? 'Primary org' : 'Set as primary'}>
                <IconButton
                  size="small"
                  onClick={(e) => handleSetPrimary(e, org.id)}
                  disabled={org.membership?.isPrimary}
                >
                  {org.membership?.isPrimary ? (
                    <StarIcon fontSize="small" color="primary" />
                  ) : (
                    <StarBorderIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          </MenuItem>
        ))}

        <Divider sx={{ my: 1 }} />

        <MenuItem onClick={handleCreateOrg}>
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Create Organization" />
        </MenuItem>

        <MenuItem onClick={handleManageOrg}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Organization Settings" />
        </MenuItem>
      </Menu>
    </>
  );
};

export default OrganizationSwitcher;
